/**
 * Session organisation, on the GATEWAY.
 *
 * Titles, pins, folders, delete and clear have always been local state,
 * replicated between devices by the terminal's own sync service — a Go
 * sidecar with its own storage, quotas and revision index, fronted by typed
 * twins on the DIME backend. openclaw 2026.8.1 owns all of it natively:
 * session labels, a gateway-side custom-group catalog, a pinned flag,
 * archive, delete and reset. So the organisation moves onto the socket the
 * terminal already holds, and the sync service goes away.
 *
 * These are deliberately thin: one function per RPC, typed params, no queue
 * and no merge. The transport is `ws.request`, which correlates the reply by
 * request id and resolves a promise — replies never reach the frame handlers,
 * so an organisation call cannot be mistaken for chat traffic. The
 * gateway-proxy parses only the connect frame and relays the rest as bytes,
 * so none of this needs anything from the backend.
 *
 * Failure is synchronous and per call, unlike the sync client's
 * fire-and-forget queue: every caller reverts its optimistic update and says
 * so. That is the point — a silent queue is what let "my other device shows
 * an older version" go unreported for weeks.
 */

import { isScopedChatId, stripTitleScope } from './session-keys';
import type { ChatSession, CustomFolder, SessionFolder } from './types';

/**
 * The subset of the gateway client these functions need. Structural rather
 * than the concrete `AgentWebSocket` so callers can pass a double, and so
 * this module stays independent of the socket's lifecycle concerns.
 */
export interface GatewayRequester {
  request<T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ): Promise<T>;
}

/**
 * One row of `sessions.list` / `sessions.subscribe`, narrowed to the fields
 * the sidebar needs. Optional throughout: the gateway is upgraded
 * independently of this client, and a row we cannot fully read should still
 * produce a usable sidebar entry.
 */
export interface GatewaySessionRow {
  readonly key?: string;
  readonly sessionId?: string;
  readonly kind?: string;
  /** Operator-set name (`sessions.patch { label }`). */
  readonly label?: string;
  /**
   * The gateway's own name for the chat when no label is set.
   *
   * NOT a generated summary, on the keys this client uses: measured against
   * a live pod, it is the raw text of the transcript's FIRST message. openclaw
   * does generate proper titles with its utility model ("BTC Perp Weekly PnL
   * Check"), but only for session keys carrying the `dashboard:` scope its own
   * dashboard uses, and it publishes those as `displayName`. The terminal
   * mints unscoped keys, so it gets the raw first message here and nothing
   * there — which is why a chat whose first message was a `/model` command
   * the client sent itself ends up named after it.
   */
  readonly derivedTitle?: string;
  /**
   * openclaw's generated title — "BTC Perp Weekly PnL Check" — written by its
   * utility model from the session's first user message.
   *
   * Populated only for keys carrying the `dashboard:` scope, which the
   * terminal now puts on chats it mints (`session-keys.ts`). On the unscoped
   * keys of chats that predate that, this is empty or merely echoes the
   * label, so it is only preferred over `derivedTitle` for a scoped row — see
   * `sessionRowTitle`.
   */
  readonly displayName?: string;
  /** Custom-group membership — the group's NAME, not an id. */
  readonly category?: string;
  readonly pinned?: boolean;
  readonly archived?: boolean;
  readonly isMain?: boolean;
  readonly updatedAt?: number | null;
  readonly lastActivityAt?: number;
  readonly lastInteractionAt?: number;
}

/** What `sessions.list` and `sessions.subscribe` answer with. */
export interface GatewaySessionsListResult {
  readonly sessions?: readonly GatewaySessionRow[];
  readonly hasMore?: boolean;
  readonly nextOffset?: number;
}

/** `sessions.subscribe` with list params answers with both at once. */
export interface GatewaySessionsSubscribeResult {
  readonly subscribed?: boolean;
  readonly list?: GatewaySessionsListResult;
}

/** One entry of the gateway's custom-group catalog. */
export interface GatewaySessionGroup {
  readonly name: string;
  readonly position: number;
}

/**
 * Organisation fields this client sets. A deliberate subset of
 * `sessions.patch`: the privileged overrides (thinking, verbose, trace,
 * elevation) need `operator.admin` and belong to no terminal control, and
 * naming them here would invite a caller to send one by accident.
 */
export interface SessionOrganisationPatch {
  /** The chat's name. `null` clears it back to the gateway's derived title. */
  readonly label?: string | null;
  /** Custom-group name, or `null` to unfile. */
  readonly category?: string | null;
  readonly pinned?: boolean;
  readonly archived?: boolean;
}

/**
 * How the terminal's chat keys map to gateway session keys.
 *
 * The terminal mints a UUID per chat and sends it as `sessionKey`; the
 * gateway files it under the sole agent as `agent:main:<uuid>`. Both forms
 * are therefore the same chat, and `sessions.*` answers with the qualified
 * one while `chat.send` was given the bare one. Callers hold bare ids, so
 * rows are matched by stripping the prefix.
 */
const AGENT_KEY_PREFIX = /^agent:[^:]+:/u;

/**
 * The bare chat id for a gateway row's key, or null when it has none.
 *
 * Also removes openclaw's `dashboard:` title scope, which the terminal adds to
 * the keys of chats it minted (see `session-keys.ts`) — so a scoped row and an
 * unscoped one both resolve to the id the UI holds.
 */
export function bareSessionId(key: string | undefined): string | null {
  if (key == null || key.length === 0) return null;
  const bare = stripTitleScope(key.replace(AGENT_KEY_PREFIX, ''));
  return bare.length > 0 ? bare : null;
}

/**
 * Whether a gateway row is one of the terminal's chats.
 *
 * The roster carries every session the agent has, which on a live pod means
 * cron runs, heartbeat wakes, sub-agent children and the agent's own main
 * session alongside the user's chats. Only `direct` rows keyed by a bare
 * UUID are ours: the synthetic kinds carry structured keys
 * (`agent:main:cron:<jobId>`), and main is the agent's own rolling thread,
 * which the terminal does not present as a chat.
 */
export function isTerminalChatRow(row: GatewaySessionRow): boolean {
  if (row.isMain === true) return false;
  if (row.kind != null && row.kind !== 'direct') return false;
  const bare = bareSessionId(row.key);
  if (bare == null) return false;
  // The agent's own thread, by its KEY rather than by the gateway remembering
  // to flag it. `agent:main:main` bares to `main`, which has no colon, so a
  // row that omits BOTH `isMain` and `kind` slipped through the checks above
  // and the test below — putting the agent's rolling thread in the user's
  // sidebar, where clear and delete would reach it.
  if (bare === 'main') return false;
  // A synthetic key keeps its colons after the agent prefix is stripped.
  return !bare.includes(':');
}

/**
 * The sidebar title for a row.
 *
 * Precedence: an explicit label the user set, then — on a chat minted with
 * title-scoped keys — openclaw's generated title, then the raw text of the
 * first message. A row with none of those is a chat that has never been sent
 * to, and the caller supplies its placeholder: the gateway has nothing to
 * offer for it and inventing text here would put "Untitled" on the wire.
 *
 * `displayName` is preferred over `derivedTitle` ONLY for a scoped row. On the
 * unscoped keys of chats that predate the scope openclaw generates nothing, so
 * whatever is in that field there is not a title — reordering it globally
 * would rename existing chats to whatever the gateway happens to be echoing.
 */
export function sessionRowTitle(row: GatewaySessionRow): string | null {
  let commandTitle: string | null = null;
  const generated = isScopedChatId(bareSessionId(row.key) ?? undefined)
    ? [row.displayName, row.derivedTitle]
    : [row.derivedTitle, row.displayName];
  for (const candidate of [row.label, ...generated]) {
    if (candidate == null || candidate.trim().length === 0) continue;
    // A generated title is only as good as the first message it was made
    // from, and for a long stretch the terminal's first message was a
    // `/model <id>` command it wrote itself — so one customer had 24 of 29
    // chats named `/model dt-a/bedrock/anthropic/opus/5`. Prefer anything
    // else. An operator-set LABEL is taken as-is regardless: if someone
    // deliberately renamed a chat to a slash command, that is their choice.
    if (candidate !== row.label && isSlashCommandText(candidate)) {
      commandTitle ??= candidate.trim();
      continue;
    }
    return candidate.trim();
  }
  // Last resort, and deliberately NOT the caller's "New Chat" placeholder.
  // Those chats have real conversations in them — the command is only their
  // FIRST message, because that is what the terminal used to send on their
  // behalf. Calling them new is a worse lie than showing an ugly name, and
  // it hides which chat is which. `deriveTitleFromTranscript` replaces this
  // with the user's own first message once the chat is read.
  return commandTitle;
}

/**
 * The title a live `sessions.changed` push may APPLY over a name the client
 * already chose — or null, meaning "leave the chat's title alone".
 *
 * The roster read on connect can take a row's title wholesale, because it is
 * building the sidebar from nothing. A push cannot: by the time one arrives
 * the chat is already named, locally, from its first message. So only two
 * things here outrank that name.
 *
 * A LABEL, because someone set it deliberately — on this device or another —
 * and openclaw stops offering a derived title once a label exists.
 *
 * A GENERATED title, on a title-scoped chat. This is the point of the scope:
 * openclaw's utility model turns the first message into "BTC Perp Weekly PnL
 * Check" a few seconds after the send, and this push is how it arrives.
 *
 * `derivedTitle` is deliberately NOT adoptable. It is the first message
 * verbatim, which is what the client already derived its own title from — so
 * adopting it would rewrite every chat's title on every push to a slightly
 * different rendering of the same text, and would undo a user's rename in the
 * window before the gateway's label lands.
 */
export function adoptableRowTitle(row: GatewaySessionRow): string | null {
  const label = row.label?.trim();
  if (label != null && label.length > 0) return label;
  if (!isScopedChatId(bareSessionId(row.key) ?? undefined)) return null;
  const generated = row.displayName?.trim();
  if (generated == null || generated.length === 0) return null;
  // A title generated from a slash command is the bug this branch was built
  // to stop putting on screen; keep whatever the client had.
  return isSlashCommandText(generated) ? null : generated;
}

/**
 * Whether text is a slash command rather than something a person said.
 *
 * Deliberately narrow — a leading `/` followed by a command word. A message
 * that merely opens with a path (`/etc/hosts is missing`) is prose, and
 * naming a chat after it is correct.
 */
export function isSlashCommandText(text: string | undefined): boolean {
  if (text == null) return false;
  return /^\/[a-z][a-z0-9-]*(\s|$)/iu.test(text.trim());
}

/**
 * Whether the gateway has NO name of any kind for this row.
 *
 * Which now means the chat has never been sent to. `derivedTitle` is the
 * text of the transcript's first message, so a row with none has no
 * messages — and rows like that exist because `sessions.patch` CREATES the
 * session when it does not exist, so seeding a new chat's model (or filing
 * it, or pinning it) claims a gateway row before any message is sent.
 *
 * That is a cleaner signal than it looks, and specifically cleaner than the
 * heuristic it replaces. While the terminal sent `/model <id>` as a chat
 * message, an unused chat DID have a first message and so a title — the
 * command — indistinguishable from a real chat that merely opened with one.
 * Now that model selection is a patch that touches no transcript, "no title
 * at all" is positive evidence of "nothing was ever said here".
 *
 * Callers must still exempt the chat the user is LOOKING at: a chat opened
 * and not yet typed in is exactly this shape, and hiding it would empty the
 * sidebar row under the cursor.
 */
export function isUntitledRow(row: GatewaySessionRow): boolean {
  return [row.label, row.derivedTitle, row.displayName].every(
    (candidate) => candidate == null || candidate.trim().length === 0,
  );
}

/** The row's last-activity stamp, for sidebar ordering. */
export function sessionRowUpdatedAt(row: GatewaySessionRow): number | null {
  for (const candidate of [
    row.lastActivityAt,
    row.lastInteractionAt,
    row.updatedAt,
  ]) {
    if (typeof candidate === 'number' && candidate > 0) return candidate;
  }
  return null;
}

// ─── Projections ───────────────────────────────────────────────────────────

/** What the roster projection needs from the caller. */
export interface ProjectSessionsOptions {
  /** The host's folder bucket (`paradex` / `paradigm`) for every row. */
  readonly folder: SessionFolder;
  /** Title for a chat the gateway has no name for — one never sent to. */
  readonly untitled: string;
  /**
   * "Now" on THIS device, for the stamp clamp below. Injectable for tests;
   * defaults to `Date.now()`.
   */
  readonly now?: number;
}

/**
 * The gateway's roster as the sidebar's session list.
 *
 * A group is identified by its NAME on the gateway (`category`), and the
 * terminal's folder id becomes that name rather than a generated uuid. One
 * fewer mapping to keep consistent, and it makes filing a chat a single
 * field copy in both directions.
 *
 * Rows arrive newest-first from `sessions.list` with `sortBy: 'updatedAt'`,
 * but the order is re-derived here anyway: the sidebar sorts on its own
 * `updatedAt`, and a row whose stamp we could not read must not float to the
 * top of the list on a zero.
 */
export function projectGatewaySessions(
  rows: readonly GatewaySessionRow[] | undefined,
  options: ProjectSessionsOptions,
): readonly ChatSession[] {
  if (!Array.isArray(rows)) return [];
  const out: ChatSession[] = [];
  for (const row of rows) {
    if (!isTerminalChatRow(row)) continue;
    if (row.archived === true) continue;
    const sessionId = bareSessionId(row.key);
    if (sessionId == null) continue;
    // CLAMPED to this device's clock. The gateway's is not ours, and a row
    // from a faster one lands in the local future — which matters because
    // the roster read exempts rows stamped after the read began (a chat the
    // read cannot have seen). An over-stamped cached row would be exempt
    // from every read for as long as the skew lasts, so a chat deleted on
    // another device would never leave this one.
    const rawUpdatedAt = sessionRowUpdatedAt(row) ?? 0;
    const updatedAt = Math.min(rawUpdatedAt, options.now ?? Date.now());
    const category = row.category?.trim();
    out.push({
      sessionId,
      folder: options.folder,
      title: sessionRowTitle(row) ?? options.untitled,
      pinned: row.pinned === true,
      // The gateway has no separate creation stamp in this projection, and
      // the sidebar only ever sorts and groups by `updatedAt`.
      createdAt: updatedAt,
      updatedAt,
      ...(category != null && category.length > 0
        ? { customFolderId: category }
        : {}),
    });
  }
  return out.sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * The gateway's group catalog as the sidebar's folders.
 *
 * `position` is the gateway's display order, which the terminal renders in
 * list order. The id is the name — see `projectGatewaySessions`.
 */
export function foldersFromGatewayGroups(
  groups: readonly GatewaySessionGroup[] | undefined,
): readonly CustomFolder[] {
  if (!Array.isArray(groups)) return [];
  return [...groups]
    .filter((g) => typeof g?.name === 'string' && g.name.trim().length > 0)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((g) => ({
      id: g.name,
      name: g.name,
      // No creation stamp on the wire; the catalog's own order is what the
      // sidebar uses, so this only has to be stable.
      createdAt: 0,
    }));
}

// ─── Reads ─────────────────────────────────────────────────────────────────

/**
 * Subscribe to session changes AND load the roster in one request.
 *
 * `sessions.subscribe` with non-empty list params answers
 * `{ subscribed: true, list }`; with `{}` it only acknowledges. The
 * subscription is per connection and covers every session, so one call also
 * covers switching chats — and it must be in place before the snapshot is
 * read, which the gateway guarantees by registering it first.
 */
export async function subscribeSessions(
  ws: GatewayRequester,
  params: Record<string, unknown> = {},
): Promise<GatewaySessionsListResult | null> {
  const res = await ws.request<GatewaySessionsSubscribeResult>(
    'sessions.subscribe',
    params,
  );
  return res?.list ?? null;
}

/** One page of the roster. `sessions.subscribe` is preferred for the first. */
export async function listSessions(
  ws: GatewayRequester,
  params: Record<string, unknown> = {},
): Promise<GatewaySessionsListResult | null> {
  return (
    (await ws.request<GatewaySessionsListResult>('sessions.list', params)) ??
    null
  );
}

/** The gateway's custom-group catalog, in display order. */
export async function listSessionGroups(
  ws: GatewayRequester,
): Promise<readonly GatewaySessionGroup[]> {
  const res = await ws.request<{ groups?: readonly GatewaySessionGroup[] }>(
    'sessions.groups.list',
  );
  return res?.groups ?? [];
}

// ─── Writes ────────────────────────────────────────────────────────────────

/**
 * Set a chat's organisation fields. Needs `operator.write`, which the
 * terminal's pre-approved proxy device already holds.
 */
export async function patchSessionOrganisation(
  ws: GatewayRequester,
  sessionKey: string,
  patch: SessionOrganisationPatch,
): Promise<void> {
  await ws.request('sessions.patch', { key: sessionKey, ...patch });
}

/** What `sessions.patch` reports back about the model it actually applied. */
export interface PatchedSessionModel {
  /** `provider/model`, composed from the gateway's own resolution. */
  readonly modelId: string | null;
}

/**
 * Set a chat's MODEL, and read back the model the gateway resolved.
 *
 * This is the write path for model selection. The alternative — sending
 * `/model <id>` as a chat message — is what the terminal used to do, and it
 * is worse in every respect: it appends two entries to the transcript (so a
 * chat whose only content is a model switch gets named after the command),
 * it starts a real agent run that then has to be muted and swept from the
 * screen, and the confirmation comes back as prose that has to be pattern-
 * matched. A comment in the provider claimed the gateway refuses
 * `sessions.patch` from webchat clients; that is not true of the gateways
 * this fleet runs, where the same call already carries every organisation
 * field.
 *
 * The response's `resolved` block is the confirmation: the gateway validates
 * the id against its model catalog, so a rejected or unknown model surfaces
 * as a request error and a silently-substituted one surfaces as a
 * `modelId` that differs from what was asked for. No read-back needed.
 */
export async function patchSessionModel(
  ws: GatewayRequester,
  sessionKey: string,
  modelId: string,
): Promise<PatchedSessionModel> {
  const res = await ws.request<{
    resolved?: { modelProvider?: string; model?: string };
  }>('sessions.patch', { key: sessionKey, model: modelId });
  const provider = res?.resolved?.modelProvider;
  const model = res?.resolved?.model;
  return {
    modelId:
      provider != null &&
      provider.length > 0 &&
      model != null &&
      model.length > 0
        ? `${provider}/${model}`
        : null,
  };
}

/**
 * Replace the ordered group catalog.
 *
 * The whole list, every time — the RPC "replaces only the name list and
 * order", so creating a group means putting the existing names plus the new
 * one. It also refuses to drop a group that still has members, which is why
 * deletion has its own call rather than being expressed as an omission here.
 */
export async function putSessionGroups(
  ws: GatewayRequester,
  names: readonly string[],
): Promise<readonly GatewaySessionGroup[]> {
  const res = await ws.request<{ groups?: readonly GatewaySessionGroup[] }>(
    'sessions.groups.put',
    { names },
  );
  return res?.groups ?? [];
}

/** Rename a group; the gateway repoints every member session server-side. */
export async function renameSessionGroup(
  ws: GatewayRequester,
  name: string,
  to: string,
): Promise<void> {
  await ws.request('sessions.groups.rename', { name, to });
}

/** Delete a group; members are unfiled server-side, not deleted. */
export async function deleteSessionGroup(
  ws: GatewayRequester,
  name: string,
): Promise<void> {
  await ws.request('sessions.groups.delete', { name });
}

/**
 * Clear a chat.
 *
 * `reason: 'new'` starts a fresh transcript under the same key. The previous
 * transcript is preserved server-side but no longer live, so it stops
 * reaching both the user and the model — which is what "clear" was always
 * taken to mean, and more than the terminal used to do: its own clear only
 * hid messages locally and left the agent's context intact.
 */
export async function resetSession(
  ws: GatewayRequester,
  sessionKey: string,
): Promise<void> {
  await ws.request('sessions.reset', { key: sessionKey, reason: 'new' });
}

/**
 * Delete a chat and its transcript.
 *
 * `expectedSessionId` is the caller-observed generation from the roster row.
 * Passing it makes the delete a compare-and-swap: if the chat has since been
 * reset or replaced, the call fails rather than removing a newer
 * conversation that happens to share the key. The gateway writes and
 * verifies a compressed archive before removing rows.
 */
export async function deleteGatewaySession(
  ws: GatewayRequester,
  sessionKey: string,
  expectedSessionId?: string,
): Promise<void> {
  await ws.request('sessions.delete', {
    key: sessionKey,
    deleteTranscript: true,
    ...(expectedSessionId != null ? { expectedSessionId } : {}),
  });
}
